import { useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/lib/AuthContext";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";

const ForgotPassword = () => {
  const { lang } = useLang();
  const { forgot } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState("");

  const t = lang === "fr" ? {
    kicker: "Compte Dataleo",
    title: "Mot de passe oublié",
    intro: "Entrez votre email pour recevoir un lien de réinitialisation. Valable 1 heure.",
    email: "Email",
    submit: "Envoyer le lien",
    sending: "Envoi…",
    ok: "Si un compte existe pour cet email, un lien de réinitialisation vient d'être envoyé.",
    back: "← Retour à la connexion",
  } : {
    kicker: "Dataleo account",
    title: "Forgot password",
    intro: "Enter your email to receive a reset link. Valid for 1 hour.",
    email: "Email",
    submit: "Send reset link",
    sending: "Sending…",
    ok: "If an account exists for this email, a reset link has just been sent.",
    back: "← Back to sign in",
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(""); setOk(false);
    const res = await forgot(email.trim(), lang);
    if (res.ok) setOk(true);
    else setError(res.error);
    setBusy(false);
  };

  return (
    <div data-testid="forgot-password-page">
      <SeoHead lang={lang} path="/forgot-password" title={t.title} description={t.intro} noindex />
      <PageHero
        kicker={t.kicker}
        title={t.title}
        description={t.intro}
        accent="yellow"
        compact
        testId="forgot-hero"
      />
      <div className="dl-container py-10 max-w-md">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="flex flex-col gap-1">
          <span className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#4B5563]">{t.email} *</span>
          <input
            data-testid="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border border-[#E5E7EB] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
          />
        </label>
        <button
          type="submit"
          disabled={busy || ok}
          data-testid="forgot-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#111827] hover:bg-[#0f172a] disabled:opacity-60 text-white px-5 py-3 text-sm font-bold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} {busy ? t.sending : t.submit}
        </button>
        {ok && (
          <div data-testid="forgot-ok" className="inline-flex items-start gap-2 text-sm text-[#00A878]">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> <span>{t.ok}</span>
          </div>
        )}
        {error && (
          <div data-testid="forgot-error" className="inline-flex items-start gap-2 text-sm text-red-700">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}
      </form>
      <div className="mt-6 text-sm">
        <Link to="/login" className="text-[#4B5563] hover:text-[#00A878]">{t.back}</Link>
      </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
